import {
  DropdownMenu,
  DropdownItem,
  FormField,
  Input,
  TextInput,
  Icon,
  colors,
  CheckboxInput,
  Button,
} from '@dev-spendesk/grapes';
import { useEffect, useState, useRef } from 'react';

import {
  type TGlobalFunctionTyped,
  useTranslation,
} from 'src/core/common/hooks/useTranslation';

import { type Dimension } from '../../approvalWorkflow';
import { useDimensionValues } from '../../hooks/useDimensionValues';
import { useWorkflowContext } from '../../state/WorkflowContext';
import { ApplyToAllSwitchField } from '../ApplyToAllSwitchField';

export type Option = {
  key: string;
  label: string;
};

type Props = {
  selectedValues: { id: string; name: string }[] | undefined | null;
  mode: 'create' | 'edit';
  onSelect: (values: { id: string; name: string }[] | null) => void;
  baseOptions: Option[];
  placeholder: string;
  dimensionName: Dimension['type'];
  valueFieldName?: string;
  isAsync?: boolean;
  onSearchAsync?: (value: string) => void;
  customFieldId?: string;
};

export const CommonSelect = ({
  selectedValues,
  mode,
  onSelect,
  baseOptions,
  placeholder,
  dimensionName,
  valueFieldName,
  isAsync,
  customFieldId,
  onSearchAsync,
}: Props) => {
  const { selectedNodeId, getSiblingNodes } = useWorkflowContext();
  const { getDimensionValues } = useDimensionValues();
  const { t } = useTranslation('global');

  // Compute sibling values (only for base nodes)
  const selectedSiblingNodesValues = selectedNodeId
    ? getSiblingNodes(selectedNodeId)
        .filter((node) => node.id !== selectedNodeId)
        .filter((node) => node.type === 'baseNode')
        .map((node) => node.data.values)
        .filter((values) => values !== undefined)
        .flat()
    : [];

  const selectedSiblingNodesValuesIds = selectedSiblingNodesValues
    .map((value) => value?.id)
    .filter(Boolean);

  const getFilteredOptions = () => {
    // For async fields, don't filter if we don't have options yet
    if (isAsync && baseOptions.length === 0) {
      return [];
    }
    return baseOptions;
  };

  const isOptionDisabled = (optionKey: string) => {
    return selectedSiblingNodesValuesIds.includes(optionKey);
  };

  const [options, setOptions] = useState<Option[]>(getFilteredOptions());
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isAllOthersSwitchDisplayed, setIsAllOthersSwitchDisplayed] =
    useState(false);
  const [isAllOthersSwitchChecked, setIsAllOthersSwitchChecked] =
    useState(false);
  const [isAutocompleteDisplayed, setIsAutocompleteDisplayed] = useState(false);
  const toggleButtonReference = useRef<HTMLButtonElement | null>(null);

  const getShouldDisplayAllOthersSwitch = (): boolean => {
    const hasNullSibling = selectedSiblingNodesValues.includes(null);
    return !hasNullSibling;
  };

  const getAllValuesInDimensionSelected = (): boolean => {
    const allDimensionValues = getDimensionValues(dimensionName, customFieldId);
    return allDimensionValues.every((value) =>
      selectedSiblingNodesValuesIds.includes(value),
    );
  };

  // Update toggle display and state based on current selection and sibling nodes.
  useEffect(() => {
    const shouldDisplayAllOthersSwitch = getShouldDisplayAllOthersSwitch();
    const allValuesInDimensionSelected = getAllValuesInDimensionSelected();
    const shouldDisplayAutocomplete =
      mode === 'edit' ||
      (mode === 'create' &&
        (!allValuesInDimensionSelected || (isAsync ?? false)));

    setIsAutocompleteDisplayed(shouldDisplayAutocomplete);
    setIsAllOthersSwitchChecked(selectedValues === null);
    setIsAllOthersSwitchDisplayed(
      shouldDisplayAllOthersSwitch || selectedValues === null,
    );
  }, [
    selectedValues,
    options.length,
    selectedSiblingNodesValues,
    dimensionName,
    customFieldId,
  ]);

  useEffect(() => {
    const newOptions = getFilteredOptions();
    // For async fields, only update if we have actual options
    if (isAsync && baseOptions.length === 0) {
      return;
    }
    // For async search, don't apply local filtering - parent handles it
    // For sync search, apply local filtering based on searchTerm
    let filteredOptions = newOptions;
    if (!isAsync && searchTerm) {
      filteredOptions = newOptions.filter((option) =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    if (JSON.stringify(filteredOptions) !== JSON.stringify(options)) {
      setOptions(filteredOptions);
    }
  }, [selectedSiblingNodesValues, baseOptions, searchTerm, isAsync]);

  const handleApplyToAllChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const checked = event.target.checked;
    setIsAllOthersSwitchChecked(checked);
    if (checked) {
      onSelect(null);
    } else {
      onSelect(
        options.map((option) => ({ id: option.key, name: option.label })),
      );
    }
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    if (isAsync) {
      onSearchAsync?.(value);
    }
  };

  const handleSelect = (option: { key: string; label: string }) => {
    if (option.key === '__search__') {
      return;
    }

    if (option.key === '__selectAll__') {
      // Toggle select all - only include enabled options
      const enabledOptions = options.filter(
        (opt) => !isOptionDisabled(opt.key),
      );
      if (selectedValues?.length === enabledOptions.length) {
        onSelect([]);
      } else {
        onSelect(
          enabledOptions.map((opt) => ({ id: opt.key, name: opt.label })),
        );
      }
      return;
    }

    if (isOptionDisabled(option.key)) {
      return;
    }

    // Toggle individual selection
    const isSelected = selectedValues?.some((value) => value.id === option.key);
    if (isSelected) {
      onSelect(
        selectedValues?.filter((value) => value.id !== option.key) || [],
      );
    } else {
      onSelect([
        ...(selectedValues || []),
        { id: option.key, name: option.label },
      ]);
    }
  };

  const getButtonLabel = () => {
    if (isAllOthersSwitchChecked) {
      return getSelectedTranslation(null, options, dimensionName, t);
    }
    if (!selectedValues || selectedValues.length === 0) {
      return placeholder;
    }
    const fullLabel = selectedValues.map((value) => value.name).join(', ');
    return fullLabel.length > 60 ? `${fullLabel.slice(0, 60)}...` : fullLabel;
  };

  // Filter options based on search term
  // For async search, don't filter locally - parent handles it via baseOptions
  let filteredOptions = options;
  if (!isAsync && searchTerm) {
    filteredOptions = options.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }

  const enabledOptions = filteredOptions.filter(
    (option) => !isOptionDisabled(option.key),
  );
  const allSelected =
    selectedValues?.length === enabledOptions.length &&
    enabledOptions.length > 0;

  const sortedOptions = [...filteredOptions].sort((a, b) => {
    const aDisabled = isOptionDisabled(a.key);
    const bDisabled = isOptionDisabled(b.key);
    if (aDisabled === bDisabled) {
      return 0;
    }
    return aDisabled ? 1 : -1;
  });

  const dropdownOptions = [
    { key: '__search__', label: '' },
    ...(enabledOptions.length > 0
      ? [
          {
            key: '__selectAll__',
            label: t('approvalWorkflows.selectAll', {
              count: enabledOptions.length,
            }),
          },
        ]
      : []),
    ...sortedOptions,
  ];

  const autocomplete = isAutocompleteDisplayed ? (
    <FormField className="text-left" label={t('approvalWorkflows.panel.is')}>
      <DropdownMenu
        className="w-full"
        keepOpenOnSelect
        options={dropdownOptions}
        renderButton={(getToggleButtonProps) => {
          const toggleProps = getToggleButtonProps({});
          const originalOnClick = toggleProps.onClick;

          return (
            <Button
              {...toggleProps}
              onClick={(event) => {
                setSearchTerm('');
                originalOnClick?.(event);
              }}
              ref={toggleButtonReference}
              variant="secondaryNeutral"
              fit="parent"
              className="flex items-center justify-between px-8"
              iconName="chevron-down"
              iconPosition="right"
              isDisabled={isAllOthersSwitchChecked}
              text={getButtonLabel()}
            />
          );
        }}
        onSelect={handleSelect}
        renderOption={(option) =>
          renderOption({
            option,
            search: searchTerm,
            setSearch: handleSearch,
            selectedValues: selectedValues || [],
            allSelected,
            placeholder:
              options.length === 0
                ? getPlaceholderTranslation(isAsync, dimensionName, t)
                : '',
            isDisabled: isOptionDisabled(option.key),
            onOptionToggle: handleSelect,
          })
        }
      />
    </FormField>
  ) : null;

  const disabledInput = (
    <FormField className="text-left" label="Is">
      <Input
        isDisabled
        value={getSelectedTranslation(
          selectedValues,
          options,
          dimensionName,
          t,
        )}
      />
    </FormField>
  );

  return (
    <>
      {(baseOptions.length > 0 || isAsync) && !isAllOthersSwitchChecked
        ? autocomplete
        : disabledInput}

      {isAllOthersSwitchDisplayed && (
        <ApplyToAllSwitchField
          onChange={handleApplyToAllChange}
          isChecked={isAllOthersSwitchChecked}
          dimensionName={dimensionName}
          valueFieldName={valueFieldName}
        />
      )}
    </>
  );
};

const renderOption = ({
  option,
  search,
  setSearch,
  selectedValues,
  allSelected,
  placeholder,
  isDisabled,
  onOptionToggle,
}: {
  option: { key: string; label: string };
  search: string;
  setSearch: (search: string) => void;
  selectedValues: { id: string; name: string }[];
  allSelected: boolean;
  placeholder: string;
  isDisabled?: boolean;
  onOptionToggle: (option: { key: string; label: string }) => void;
}) => {
  if (option.key === '__search__') {
    return (
      // eslint-disable-next-line jsx-a11y/no-static-element-interactions
      <div
        className="mb-8 w-full"
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <TextInput
          fit="parent"
          value={search}
          leftAddon={
            <Icon
              className="ml-8"
              name="magnifying-glass"
              color={colors.contentDecorativeIcon}
            />
          }
          placeholder={placeholder || 'Search'}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setSearch(event.target.value)
          }
          onKeyDown={(event) => event.stopPropagation()}
        />
      </div>
    );
  }

  if (option.key === '__selectAll__') {
    return (
      <DropdownItem
        label={option.label}
        prefix={
          <CheckboxInput
            isChecked={allSelected}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              event.stopPropagation();
              onOptionToggle(option);
            }}
            onClick={(event: React.MouseEvent) => event.stopPropagation()}
          />
        }
      />
    );
  }

  const isSelected = selectedValues.some((value) => value.id === option.key);

  return (
    <DropdownItem
      label={option.label}
      isDisabled={isDisabled}
      prefix={
        <CheckboxInput
          isChecked={isSelected}
          isDisabled={isDisabled}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            event.stopPropagation();
            if (!isDisabled) {
              onOptionToggle(option);
            }
          }}
          onClick={(event: React.MouseEvent) => event.stopPropagation()}
        />
      }
    />
  );
};

const getPlaceholderTranslation = (
  isAsync: boolean | undefined,
  dimensionName: Dimension['type'],
  t: TGlobalFunctionTyped,
) => {
  if (dimensionName === 'costCenter') {
    return t('approvalWorkflows.dimensions.costCenter.selectACostCenter');
  }
  if (dimensionName === 'spendType') {
    return t('approvalWorkflows.dimensions.spendType.selectASpendType');
  }
  if (dimensionName === 'customField' && isAsync) {
    return t(
      'approvalWorkflows.dimensions.customField.selectACustomFieldAsync',
    );
  }
  if (dimensionName === 'customField' && !isAsync) {
    return t('approvalWorkflows.dimensions.customField.selectACustomField');
  }
  if (dimensionName === 'expenseCategory' && isAsync) {
    return t(
      'approvalWorkflows.dimensions.expenseCategory.selectAnExpenseCategoryAsync',
    );
  }
  if (dimensionName === 'expenseCategory' && !isAsync) {
    return t(
      'approvalWorkflows.dimensions.expenseCategory.selectAnExpenseCategory',
    );
  }
  if (dimensionName === 'team') {
    return t('approvalWorkflows.dimensions.team.selectATeam');
  }

  return t('approvalWorkflows.dimensions.searchForValues');
};

const getSelectedTranslation = (
  selectedValues: { id: string; name: string }[] | undefined | null,
  options: Option[],
  dimensionName: Dimension['type'],
  t: TGlobalFunctionTyped,
) => {
  if (selectedValues === null) {
    if (dimensionName === 'costCenter') {
      return t('approvalWorkflows.dimensions.costCenter.allOtherCostCenters');
    }
    if (dimensionName === 'spendType') {
      return t('approvalWorkflows.dimensions.spendType.allOtherSpendTypes');
    }
    if (dimensionName === 'customField') {
      return t('approvalWorkflows.dimensions.customField.allOtherCustomFields');
    }
    if (dimensionName === 'expenseCategory') {
      return t(
        'approvalWorkflows.dimensions.expenseCategory.allOtherExpenseCategories',
      );
    }
  }
  if (selectedValues && selectedValues.length > 0) {
    return selectedValues
      .map((value) => options.find((option) => option.key === value.id)?.label)
      .join(', ');
  }
  return '';
};
